/**
 * Minimal GetMap query for the maps BlueSky proxy.
 * Proxy forces SERVICE/REQUEST/VERSION/LAYERS/STYLES/SRS/FORMAT/TRANSPARENT
 * and WIDTH/HEIGHT=256 (GWC grid) server-side; clients only send BBOX.
 */
const buildBlueSkyWmsUrl = (mapsUrl: string, bbox: string): string => {
  const base = mapsUrl.replace(/\/$/, '');
  const url = new URL(`${base}/imagery/bluesky/wms`);
  url.searchParams.set('BBOX', bbox);
  // Mapbox GL needs the literal placeholder unescaped in tile templates.
  return url.toString().replace('%7Bbbox-epsg-3857%7D', '{bbox-epsg-3857}');
};

/** Build a Mapbox WMS tile template via the maps BlueSky proxy (no client uuid). */
export const buildBlueSkyWmsTileTemplate = (mapsUrl: string): string => {
  return buildBlueSkyWmsUrl(mapsUrl, '{bbox-epsg-3857}');
};

/** Build a BlueSky WMS GetMap URL for a fixed mercator bbox. */
export const buildBlueSkyWmsStaticUrl = (mapsUrl: string, bbox3857: string): string => {
  return buildBlueSkyWmsUrl(mapsUrl, bbox3857);
};

/** BlueSky only publishes UK imagery; keep Mapbox and offline packs from requesting tiles elsewhere. */
export const BLUESKY_BOUNDS: [number, number, number, number] = [-11, 49, 2.5, 61];
/** Below this the UK is a few pixels wide; tiles there are not worth fetching. */
export const BLUESKY_MIN_ZOOM = 5;

const EARTH_RADIUS_M = 6378137;
const ORIGIN_SHIFT = Math.PI * EARTH_RADIUS_M;

/**
 * EPSG:3857 bbox for a Web Mercator XYZ tile (y=0 at north), as
 * `minX,minY,maxX,maxY` for WMS 1.1.1 BBOX.
 */
export const xyzTileToEpsg3857Bbox = (z: number, x: number, y: number): string => {
  const n = 2 ** z;
  const tileSize = (2 * ORIGIN_SHIFT) / n;
  const minX = -ORIGIN_SHIFT + x * tileSize;
  const maxX = -ORIGIN_SHIFT + (x + 1) * tileSize;
  const maxY = ORIGIN_SHIFT - y * tileSize;
  const minY = ORIGIN_SHIFT - (y + 1) * tileSize;
  return `${minX},${minY},${maxX},${maxY}`;
};
